import * as React from "react";
import { StatusIndicator } from "../display/StatusIndicator";

import { Text } from "../display/Text";
import "./banner.css";

/**
 * Banner, BannerContent, BannerActions and LinearProgress — the feedback surfaces, replacing
 * `@salt-ds/core`'s behind `alert.tsx`, `alert-callout.tsx` and `progress.tsx`.
 *
 * ── THE BANNER'S ICON IS aria-hidden, AND THAT IS NOT AN OVERSIGHT ────────────────────
 *
 * The status icon repeats what the banner's TEXT already says. Leaving it readable makes a
 * screen reader announce "error" and then read an error message that begins "Error:" — the same
 * information twice, from two elements. The icon is decoration for a sighted reader; the words
 * carry the meaning for everyone.
 *
 * `StatusIndicator` is still the vendor's, because it resolves through Salt's semantic icon
 * registry — the icon layer is deliberately still Salt's, so an app that swaps its icon set
 * swaps these too.
 */
export type BannerStatus = "info" | "error" | "warning" | "success";
export type BannerVariant = "primary" | "secondary";

export interface BannerProps extends React.HTMLAttributes<HTMLDivElement> {
  status?: BannerStatus;
  variant?: BannerVariant;
}

export const Banner = React.forwardRef<HTMLDivElement, BannerProps>(function Banner(
  { children, className, status = "info", variant = "primary", ...rest },
  ref,
) {
  const classes = ["saltBanner", `saltBanner-${status}`, `saltBanner-${variant}`];
  if (className) classes.push(className);

  return (
    <div className={classes.join(" ")} ref={ref} {...rest}>
      <StatusIndicator aria-hidden className="saltBanner-icon" status={status} />
      {children}
    </div>
  );
});

export type BannerContentProps = React.HTMLAttributes<HTMLDivElement>;

export const BannerContent = React.forwardRef<HTMLDivElement, BannerContentProps>(
  function BannerContent({ className, ...rest }, ref) {
    return (
      <div
        className={className ? `saltBannerContent ${className}` : "saltBannerContent"}
        ref={ref}
        {...rest}
      />
    );
  },
);

export type BannerActionsProps = React.HTMLAttributes<HTMLDivElement>;

export const BannerActions = React.forwardRef<HTMLDivElement, BannerActionsProps>(
  function BannerActions({ className, ...rest }, ref) {
    return (
      <div
        className={className ? `saltBannerActions ${className}` : "saltBannerActions"}
        ref={ref}
        {...rest}
      />
    );
  },
);

export interface LinearProgressProps extends React.HTMLAttributes<HTMLDivElement> {
  hideLabel?: boolean;
  max?: number;
  min?: number;
  value?: number;
  bufferValue?: number;
}

export const LinearProgress = React.forwardRef<HTMLDivElement, LinearProgressProps>(
  function LinearProgress(
    { bufferValue, className, hideLabel = false, max = 100, min = 0, value, ...rest },
    ref,
  ) {
    /*
     * INDETERMINATE MEANS "NO VALUE AT ALL", not "value is zero". `value === undefined` is the
     * test, deliberately — a determinate bar legitimately sits at 0, and treating that as
     * indeterminate would replace a truthful empty bar with a perpetual animation that says
     * work is happening when none is.
     */
    const isIndeterminate = value === undefined && bufferValue === undefined;
    const progress = value === undefined ? 0 : ((value - min) / (max - min)) * 100;
    const buffer = bufferValue === undefined ? 0 : ((bufferValue - min) / (max - min)) * 100;

    return (
      <div
        aria-valuemax={max}
        aria-valuemin={min}
        // Omitted entirely when indeterminate: `aria-valuenow="0"` would announce "0 percent"
        // for something whose progress is genuinely unknown.
        aria-valuenow={value === undefined ? undefined : Math.round(value)}
        className={className ? `saltLinearProgress ${className}` : "saltLinearProgress"}
        ref={ref}
        role="progressbar"
        {...rest}
      >
        <div className="saltLinearProgress-barContainer">
          <div
            className={
              isIndeterminate
                ? "saltLinearProgress-bar saltLinearProgress-indeterminate"
                : "saltLinearProgress-bar"
            }
            // No width while indeterminate — the animation owns the geometry, and a fixed width
            // would pin the travelling bar in place.
            style={{ width: isIndeterminate ? undefined : `${progress}%` }}
          />
          {bufferValue && bufferValue > 0 ? (
            <div className="saltLinearProgress-buffer" style={{ width: `${buffer}%` }} />
          ) : null}
          <div className="saltLinearProgress-track" />
        </div>
        {!hideLabel ? (
          <Text className="saltLinearProgress-progressLabel" styleAs="h2">
            {isIndeterminate ? "— %" : `${Math.round(progress)} %`}
          </Text>
        ) : null}
      </div>
    );
  },
);
